import { useState, useEffect, useCallback } from "react";
import { PacmanLoader } from "react-spinners";
import Select from "react-select";
import ResultTable from "../Results/ResultTable";
import StatusBadge from "../Badges/StatusBadge";
import { Row, Col, Button } from "reactstrap";
import FocusLossModal from "../Modal/FocusLossModal";
import { FormattedMessage, useIntl } from "react-intl";
import CourseModulesResultsBar from "../Results/CourseModulesResultsBar";
import { intervalToDuration } from "date-fns";

import {
  formatDate,
  toDate,
  dateIsAfter,
  compareDatesDesc,
} from "../../Utilities/dateUtils";

import ClassResultQuestionOverview from "../Results/ClassResultQuestionOverview";
import { getWinOptions } from "../../Utilities/moduleHelper";
import { LW_COURSE_API_URL } from "../../constants/api.constants";
import StudentResultQuestionOverview from "../Results/StudentResultQuestionOverview";
import {
  GetCoursesByClassId,
  GetFinalExamVariantsForCourse,
} from "../../actions/teacherQueries";
import { useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLayerGroup,
  faBookOpen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import FinalExamSourceIcon from "../Badges/FinalExamSourceIcon";
import useRemoveFinalExamVariantAccess from "../../hooks/useRemoveFinalExamVariantAccess";
import {
  groupClassFinalExamVariants,
  groupStudentFinalExamVariants,
} from "../../Utilities/groupFinalExamVariants";
import { FINAL_EXAM_TYPE } from "../../constants/ConstantVars";

function ClassResultOverview({ classData }) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const classCoursesData = GetCoursesByClassId(classData.id);

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [resultTableData, setResultTableData] = useState(null);
  const [primaryModulesData, setPrimaryModulesData] = useState({});
  const [secondaryModulesData, setSecondaryModulesData] = useState({});
  const [courseId, setCourseId] = useState(null);
  const [moduleId, setModuleId] = useState(null);
  const [courseName, setCourseName] = useState(null);
  const [moduleName, setModuleName] = useState(null);
  const [showClassResult, setShowClassResult] = useState(false);
  const [classResultIsOpen, setClassResultIsOpen] = useState(false);
  const [showStudentResult, setShowStudentResult] = useState(false);
  const [studentForScormResults, setStudentForScormResults] = useState({});
  const [studentBestResultIsOpen, setStudentBestResultIsOpen] = useState(false);
  const [studentLatestResultIsOpen, setStudentLatestResultIsOpen] =
    useState(false);
  const [isGy25FinalExam, setIsGy25FinalExam] = useState(false);
  const [finalExamVariantId, setFinalExamVariantId] = useState(null);
  const [focusLossModal, setFocusLossModal] = useState({
    isOpen: false,
    runData: [],
    label: "",
    moduleName: "",
    isFinalExam: false,
  });
  const finalExamVariants = GetFinalExamVariantsForCourse(
    classData.id,
    selectedCourse?.id
  );

  const { setRemoveAccessPrompt, prompt: invalidateAccessPrompt } =
    useRemoveFinalExamVariantAccess(() => {
      queryClient.invalidateQueries({
        queryKey: ["FetchCoursesByClassId", classData.id],
      });
    });

  const toggle = () => setClassResultIsOpen(!classResultIsOpen);
  const toggleLatestStudentResult = () =>
    setStudentLatestResultIsOpen(!studentLatestResultIsOpen);

  const toggleBestStudentResult = () =>
    setStudentBestResultIsOpen(!studentBestResultIsOpen);

  const closeFocusLossModal = () =>
    setFocusLossModal((prev) => ({ ...prev, isOpen: false }));

  const viewAt2Result = (studentEmail, currentCourseId, currentModuleId) => {
    var winOptions = getWinOptions;
    window.open(
      `${LW_COURSE_API_URL}/Home/ViewModuleResult?` +
        "email=" +
        encodeURIComponent(studentEmail) +
        "&courseId=" +
        currentCourseId +
        "&moduleId=" +
        currentModuleId,
      "",
      winOptions
    );
  };

  const viewScormResultForClass = (
    courseId,
    moduleId,
    courseName,
    moduleName
  ) => {
    setShowClassResult(true);
    setShowStudentResult(false);
    setCourseId(courseId);
    setModuleId(moduleId);
    setCourseName(courseName);
    setModuleName(moduleName);
    setStudentBestResultIsOpen(false);
    setStudentLatestResultIsOpen(false);
    setClassResultIsOpen(true);
    setIsGy25FinalExam(false);
    setFinalExamVariantId(null);
    window.scrollTo({
      top: 300,
      behavior: "smooth",
    });
  };

  const viewScormResultForStudent = (
    courseId,
    moduleId,
    courseName,
    moduleName,
    studentId,
    studentName
  ) => {
    setShowStudentResult(true);
    setShowClassResult(false);
    setCourseId(courseId);
    setModuleId(moduleId);
    setCourseName(courseName);
    setModuleName(moduleName);
    setStudentForScormResults({ name: studentName, id: studentId });
    setStudentBestResultIsOpen(true);
    setStudentLatestResultIsOpen(true);
    setIsGy25FinalExam(false);
    setFinalExamVariantId(null);
    window.scrollTo({
      top: 300,
      behavior: "smooth",
    });
  };

  const viewGy25FinalExamResultForStudent = (
    courseId,
    moduleId,
    moduleName,
    courseName,
    variantId,
    studentName,
    studentId
  ) => {
    setShowStudentResult(true);
    setShowClassResult(false);
    setCourseId(courseId);
    setModuleId(moduleId);
    setCourseName(courseName);
    setModuleName(moduleName);
    setStudentForScormResults({ name: studentName, id: studentId });
    setStudentBestResultIsOpen(true);
    setStudentLatestResultIsOpen(true);
    setIsGy25FinalExam(true);
    setFinalExamVariantId(variantId);
    window.scrollTo({
      top: 300,
      behavior: "smooth",
    });
  };

  const viewGy25FinalExamResultForClass = (
    courseId,
    moduleId,
    moduleName,
    courseName,
    variantId
  ) => {
    setShowStudentResult(false);
    setShowClassResult(true);
    setCourseId(courseId);
    setModuleId(moduleId);
    setCourseName(courseName);
    setModuleName(moduleName);
    setStudentBestResultIsOpen(true);
    setStudentLatestResultIsOpen(true);
    setIsGy25FinalExam(true);
    setFinalExamVariantId(variantId);
    window.scrollTo({
      top: 300,
      behavior: "smooth",
    });
  };

  const sortStudentsByName = (tableData) => {
    const parentIds = new Set(
      tableData
        .filter(
          (item) =>
            item.parentId === null ||
            item.id.startsWith("classExam") ||
            item.id.startsWith("stuExam")
        )
        .map((item) => item.id)
    );

    const students = tableData.filter(
      (item) =>
        item.studentId && item.parentId !== null && !parentIds.has(item.id)
    );

    const sortedStudents = students.sort((a, b) =>
      (a.sortName || "").localeCompare(b.sortName || "")
    );

    const studentsToReplace = tableData
      .map((item, index) => ({ item, index }))
      .filter(
        (s) =>
          s.item.studentId &&
          s.item.parentId !== null &&
          !parentIds.has(s.item.id)
      );

    const sortedResult = [...tableData];

    studentsToReplace.forEach((s, i) => {
      sortedResult[s.index] = sortedStudents[i];
    });

    return sortedResult;
  };

  const buildExamRowForClass = useCallback(
    (variant, type, mod, currentCourse) => {
      const isParent = type === FINAL_EXAM_TYPE.PARENT;

      const icon = isParent ? faBookOpen : faLayerGroup;
      const idPrefix = isParent ? "classParentExam" : "classChildExam";
      const parentIdPrefix = isParent ? "classExam" : "classParentExam";

      const showResult = variant?.accesses?.some((a) => a.isSubmitted) ? (
        <Button
          className="btn btn-outline-primary btn-sm"
          style={{ background: "transparent" }}
          onClick={() => {
            viewGy25FinalExamResultForClass(
              currentCourse.id,
              mod.moduleId,
              variant.name,
              currentCourse.courseName,
              variant.id
            );

            queryClient.invalidateQueries({ queryKey: [
              "FetchCoursesByClassId",
              classData.id,
            ] });
          }}
        >
          <FormattedMessage id="ShowResult" />
        </Button>
      ) : null;

      return {
        id: idPrefix + variant.id,
        parentId:
          parentIdPrefix + (isParent ? mod.moduleId : variant.parentExamId),
        name: (
          <>
            <span
              title={
                isParent
                  ? intl.formatMessage({ id: "MainExam" })
                  : intl.formatMessage({ id: "Retest" })
              }
            >
              <FontAwesomeIcon
                icon={icon}
                className="exam-icon me-2 text-primary"
              />
            </span>
            {variant.name}
          </>
        ),
        sortName: variant.name.toLowerCase(),
        showResult,
      };
    },
    [classData.id, intl, queryClient]
  );

  const buildExamRowForStudent = useCallback(
    (variant, type, mod, currentCourse, stud) => {
      const isParent = type === FINAL_EXAM_TYPE.PARENT;

      const icon = isParent ? faBookOpen : faLayerGroup;
      const idPrefix = isParent ? "studentParentExam" : "studentChildExam";
      const parentId = isParent
        ? "student-" + stud.studentId
        : "studentParentExam-" + variant.parentId + "-" + stud.studentId;

      const scorePercentage =
        variant.score != null && variant.maxScore > 0
          ? Math.round((variant.score / variant.maxScore) * 100)
          : 0;

      const showButton =
        (!isParent || !variant.isDerivedParent) &&
        variant.status !== "NotStarted";

      const showResult = (
        <div className="d-flex flex-nowrap align-items-center justify-content-start gap-2">
          {showButton ? (
            <Button
              className="btn btn-outline-primary btn-sm"
          style={{ background: "transparent" }}
              onClick={() => {
                viewGy25FinalExamResultForStudent(
                  currentCourse.id,
                  mod.moduleId,
                  variant.name,
                  currentCourse.courseName,
                  variant.variantId,
                  stud.name,
                  stud.studentId,
                );

                queryClient.invalidateQueries({
                  queryKey: ["FetchCoursesByClassId", classData.id],
                });
              }}
            >
              <FormattedMessage id="ShowResult" />
            </Button>
          ) : variant.isFromAnotherSchool ? (
            <Button className="btn btn-outline-primary btn-sm invisible">
              <FormattedMessage id="ShowResult" />
            </Button>
          ) : null}
          {variant.isFromAnotherSchool && (
            <Button
              outline
              color="danger"
              size="sm"
              onClick={() => setRemoveAccessPrompt(variant.accessId)}
              title={intl.formatMessage({ id: "Delete" })}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          )}
        </div>
      );

      return {
        id: idPrefix + "-" + variant.variantId + "-" + stud.studentId,
        parentId: parentId,
        name: (
          <span className={!isParent ? "ms-4" : ""}>
            <FinalExamSourceIcon
              isFromAnotherSchool={variant.isFromAnotherSchool}
              isFromAnotherClass={variant.isFromAnotherClass}
            />
            <span
              title={
                isParent
                  ? intl.formatMessage({ id: "MainExam" })
                  : intl.formatMessage({ id: "Retest" })
              }
            >
              <FontAwesomeIcon
                icon={icon}
                className="exam-icon me-2 text-primary"
              />
            </span>
            {variant.name}
          </span>
        ),
        sortName: variant.name.toLowerCase(),
        showResult: showResult,
        status: ((isParent && !variant.isDerivedParent) || !isParent) && (
          <StatusBadge status={variant.status} isGy25FinalExam={true} />
        ),
        score:
          variant.score != null
            ? `${variant.score}/${variant.maxScore ?? ""} (${scorePercentage}%)`
            : null,
        nbrOfAttempts: variant.lastRun != null ? 1 : null,
        lastRun: variant.lastRun ? formatDate(variant.lastRun, "PPp") : null,
      };
    },
    [classData.id, intl, queryClient, setRemoveAccessPrompt],
  );

  const setChartData = useCallback(
    (course) => {
      const currentCourse = classCoursesData.data?.find(
        (c) => c.id === course.id
      );
      if (!currentCourse) return;

      const sortedModules = [...currentCourse.modules]
        .filter((m) => !m.isPreview)
        .sort((a, b) => a.sortOrder - b.sortOrder);

      const createChart = () => ({
        labels: [],
        datasets: [
          {
            label: intl.formatMessage({ id: "Done" }),
            value: "Done",
            data: [],
            backgroundColor: "#65C587",
          },
          {
            label: intl.formatMessage({ id: "InProgress" }),
            value: "InProgress",
            data: [],
            backgroundColor: "#FDC929",
          },
          {
            label: intl.formatMessage({ id: "NotStarted" }),
            value: "NotStarted",
            data: [],
            backgroundColor: "grey",
            border: "1px solid black",
          },
        ],
      });

      const addChartValues = (
        chart,
        { label, done, inProgress, notStarted }
      ) => {
        chart.labels.push(label);
        const get = (v) => chart.datasets.find((d) => d.value === v);

        get("Done").data.push(done);
        get("InProgress").data.push(inProgress);
        get("NotStarted").data.push(notStarted);
      };

      const countStatuses = (students, statusGetter, isFinalExam) => {
        return students.reduce(
          (acc, stud) => {
            const status = statusGetter(stud) ?? "NotStarted";

            if (isFinalExam && status === "Done") {
              acc.inProgress++;
            } else if (status === "Done" || status === "Completed") {
              acc.done++;
            } else if (status === "InProgress") {
              acc.inProgress++;
            } else if (status === "NotStarted") {
              acc.notStarted++;
            }
            return acc;
          },
          { done: 0, inProgress: 0, notStarted: 0 }
        );
      };

      let primaryChartData = createChart();
      let secondaryChartData = createChart();
      const half = Math.ceil(currentCourse.modules.length / 2);

      sortedModules.forEach((mod, i) => {
        if (mod.isGy25 && !mod.isFactModule && !mod.isFinalExam && mod.moduleCoupleId) return;

        const isGy25FactModule =
          mod.isGy25 && mod.isFactModule && !mod.isFinalExam;

        const coupledTestMod = isGy25FactModule && mod.moduleCoupleId != null
            ? sortedModules.find(
                (s) =>
                  s.moduleCoupleId === mod.moduleCoupleId &&
                  s.moduleId !== mod.moduleId &&
                  s.isGy25 &&
                  !s.isFactModule &&
                !s.isFinalExam
              )
            : null;

        let counts;

        if (mod.isGy25 && mod.isFinalExam) {
          counts = countStatuses(mod.students, (s) => s.finalExamStatus, true);
        } else {
          const studentsToCount = coupledTestMod
            ? coupledTestMod.students
            : mod.students;

          counts = countStatuses(studentsToCount, (s) => s.status, false);
        }

        if (i < half) {
          addChartValues(primaryChartData, { label: mod.name, ...counts });
        } else {
          addChartValues(secondaryChartData, { label: mod.name, ...counts });
        }
      });

      setPrimaryModulesData(primaryChartData);
      setSecondaryModulesData(secondaryChartData);
    },
    [classCoursesData.data, intl]
  );

  const setResultData = useCallback(
    (course) => {
      let tableData = [];

      const buildFocusLossCell = (
        focusLosses,
        label,
        moduleName,
        isFinalExam = false,
      ) => {
        if (!focusLosses) return null;
        const total = focusLosses.length;
        if (total === 0) return null;

        const formatDuration = (ms, autoSubmit) => {
          if (ms == null) return autoSubmit ? "" : "-";
          const {
            hours = 0,
            minutes = 0,
            seconds = 0,
          } = intervalToDuration({ start: 0, end: Math.ceil(ms / 1000) * 1000 });

          if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
          if (minutes > 0) return `${minutes}m ${seconds}s`;

          return `${seconds}s`;
        };

        const runData = [...focusLosses]
          .sort(
            (a, b) =>
              a.runNumber - b.runNumber ||
              compareDatesDesc(a.lostFocus, b.lostFocus),
          )
          .map((loss) => ({
            runNumber: loss.runNumber,
            formattedTime: formatDate(
              loss.lostFocus,
              "d MMM yyyy HH:mm:ss",
            ),
            formattedDuration: formatDuration(loss.durationMs, loss.autoSubmit),
            durationMs: loss.durationMs,
            focusControlType: loss.focusControlType,
            autoSubmit: loss.autoSubmit,
          }));

        return (
          <span
            className="focus-loss-modal__trigger"
            onClick={() =>
              setFocusLossModal({
                isOpen: true,
                runData,
                label,
                moduleName,
                isFinalExam,
              })
            }
          >
            {total}
          </span>
        );
      };

      let currentCourse = classCoursesData.data?.find(
        (c) => c.id === course.id
      );
      let sortedModules = currentCourse.modules
        .filter((m) => !m.isPreview)
        .sort((a, b) => {
          return a.sortOrder - b.sortOrder;
        });

      sortedModules.forEach((mod, i) => {
        if (mod.isGy25 && !mod.isFactModule && !mod.isFinalExam && mod.moduleCoupleId) return;

        const isGy25FactModule =
          mod.isGy25 && mod.isFactModule && !mod.isFinalExam;

        let coupledTestMod = null;

        if (isGy25FactModule && mod.moduleCoupleId != null) {
          coupledTestMod = sortedModules.find(
            (s) =>
              s.moduleCoupleId === mod.moduleCoupleId &&
              s.moduleId !== mod.moduleId &&
              s.isGy25 &&
              !s.isFactModule &&
              !s.isFinalExam
          );
        }

        if (mod.isGy25 && mod.isFinalExam) {
          tableData.push({
            id: "classExam" + mod.moduleId,
            parentId: null,
            name: <FormattedMessage id="FinalExam_Class_Overview" />,
          });
          tableData.push({
            id: "stuExam" + mod.moduleId,
            parentId: null,
            name: <FormattedMessage id="FinalExam_Student_Overview" />,
          });
        } else {
          let module = {
            id: "m" + mod.moduleId,
            parentId: null,
            name: mod.name,
            sortName: mod.name.toLowerCase(),
            showResult:
              mod.isFactModule && !coupledTestMod ? (
                <></>
              ) : mod.moduleType !== 1 ? (
                <Button
                  className="btn btn-outline-primary btn-sm"
                  style={{ background: "transparent" }}
                  onClick={() => {
                    viewScormResultForClass(
                      currentCourse.id,
                      coupledTestMod?.moduleId || mod.moduleId,
                      currentCourse.courseName,
                      mod.name
                    );
                    queryClient.invalidateQueries({ queryKey: [
                      "FetchCoursesByClassId",
                      classData.id,
                    ] });
                  }}
                >
                  <FormattedMessage id="ShowResult" />
                </Button>
              ) : null,
          };
          tableData.push(module);
        }

        if (mod.isGy25 && mod.isFinalExam) {
          //class overview
          if (!finalExamVariants.data) return;

          const classExamMap = groupClassFinalExamVariants(
            finalExamVariants.data
          );

          const rowsForClass = [];

          if (classExamMap.length > 0) {
            for (const { parent, children } of classExamMap) {
              let parentRow = buildExamRowForClass(
                parent,
                FINAL_EXAM_TYPE.PARENT,
                mod,
                currentCourse
              );

              rowsForClass.push(parentRow);

              children.forEach((child) => {
                let childRow = buildExamRowForClass(
                  child,
                  FINAL_EXAM_TYPE.CHILD,
                  mod,
                  currentCourse
                );

                rowsForClass.push(childRow);
              });
            }
          }

          tableData.push(...rowsForClass);

          //student overview
          mod.students.forEach((stud) => {
            const studentToAdd = {
              studentId: stud.studentId,
              id: "student-" + stud.studentId,
              parentId: "stuExam" + mod.moduleId,
              name: stud.name,
              sortName: stud.name.toLowerCase(),
              showResult: null,
              status: stud.status ? (
                <StatusBadge status={stud.status} isGy25FinalExam={true} />
              ) : null,
              score: null,
              nbrOfAttempts: null,
              focusLostCount: (() => {
                const accessIds = new Set(
                  (stud.finalExamVariants ?? []).map((v) => v.accessId),
                );
                const count =
                  stud.focusLosses?.filter((f) =>
                    accessIds.has(f.finalExamVariantAccessId),
                  ).length ?? 0;
                return count > 0 ? count : null;
              })(),
              lastRun: null,
            };

            tableData.push(studentToAdd);

            const studentExamMap = groupStudentFinalExamVariants(
              stud.finalExamVariants
            );

            if (studentExamMap.length > 0) {
              for (const { parent, children } of studentExamMap) {
                let parentRow = buildExamRowForStudent(
                  parent,
                  FINAL_EXAM_TYPE.PARENT,
                  mod,
                  currentCourse,
                  stud
                );

                if (!parent.isDerivedParent) {
                  const parentFocusLosses =
                    stud.focusLosses?.filter(
                      (l) =>
                        l.studentId === stud.studentId &&
                        l.finalExamVariantAccessId === parent.accessId,
                    ) ?? null;
                  parentRow.focusLostCount = buildFocusLossCell(
                    parentFocusLosses,
                    stud.name,
                    parent.name,
                    true,
                  );
                }

                tableData.push(parentRow);

                children.forEach((child) => {
                  let childRow = buildExamRowForStudent(
                    child,
                    FINAL_EXAM_TYPE.CHILD,
                    mod,
                    currentCourse,
                    stud,
                  );

                  const childFocusLosses =
                    stud.focusLosses?.filter(
                      (l) =>
                        l.studentId === stud.studentId &&
                        l.finalExamVariantAccessId === child.accessId,
                    ) ?? null;
                  childRow.focusLostCount = buildFocusLossCell(
                    childFocusLosses,
                    stud.name,
                    child.name,
                    true,
                  );

                  tableData.push(childRow);
                });
              }
            }
          });
        } else {
          mod.students.forEach((stud) => {
            let studentToAdd = stud;

            const studentTestMod =
              coupledTestMod?.students?.find(
                (s) => s.studentId === studentToAdd.studentId
              ) || null;

            const getStatusBadge = () => {
              if (mod.isGy25 && mod.isFinalExam) {
                return (
                  <StatusBadge
                    status={studentToAdd.status}
                    isGy25FinalExam={true}
                  />
                );
              }

              return coupledTestMod ? (
                <>
                  <StatusBadge
                    status={studentToAdd?.status || "NotStarted"}
                    title={intl.formatMessage({ id: "StatusForFactModule" })}
                  />
                  <StatusBadge
                    status={studentTestMod?.status || "NotStarted"}
                    title={intl.formatMessage({ id: "StatusForTestModule" })}
                    isTestModule={true}
                  />
                </>
              ) : (
                <StatusBadge status={studentToAdd.status || "NotStarted"} />
              );
            };

            const getModuleScore = () => {
              const { score = 0, maxScore = 0 } =
                coupledTestMod && studentTestMod
                  ? studentTestMod
                  : studentToAdd;

              const percentage =
                coupledTestMod && studentTestMod
                  ? studentTestMod.scoreInPercent
                  : studentToAdd.scoreInPercent;

              return `${score}/${maxScore} (${percentage}%)`;
            };

            const getNbrOfAttempt = () => {
              if (studentTestMod && studentTestMod.nbrOfAttempts > 0) {
                return studentTestMod.nbrOfAttempts;
              }
              const attempts = studentToAdd.nbrOfAttempts;
              return attempts > 0 ? attempts : null;
            };

            const getActivityPoints = () => {
              if (mod.isGy25 && mod.isFinalExam) return null;

              const points = studentToAdd.activityPoints ?? 0;
              const maxPoints = studentToAdd.maxActivityPoints ?? 0;

              const percentage =
                maxPoints > 0 ? Math.round((points / maxPoints) * 100) : 0;

              return `${points}/${maxPoints} (${percentage}%)`;
            };

            const getLastRun = () => {
              const factModLastRun = studentToAdd.lastRun
                ? toDate(studentToAdd.lastRun)
                : null;
              const testModLastRun =
                coupledTestMod && studentTestMod?.lastRun
                  ? toDate(studentTestMod.lastRun)
                  : null;

              if (!factModLastRun && !testModLastRun) return null;

              let lastRun;

              if (factModLastRun && testModLastRun) {
                lastRun = dateIsAfter(testModLastRun, factModLastRun)
                  ? testModLastRun
                  : factModLastRun;
              } else {
                lastRun = factModLastRun || testModLastRun;
              }

              if (!lastRun) return null;

              let title = "";

              if (isGy25FactModule) {
                title =
                  lastRun === factModLastRun
                    ? intl.formatMessage({ id: "LastRun_FactModule" })
                    : intl.formatMessage({ id: "LastRun_TestModule" });
              }

              return <span title={title}>{formatDate(lastRun, "PPp")}</span>;
            };

            const renderButton = () => (
              <Button
                className="btn btn-outline-primary btn-sm"
                style={{ background: "transparent" }}
                onClick={() => {
                  if (mod.moduleType === 1) {
                    viewAt2Result(stud.email, currentCourse.id, mod.moduleId);
                  } else {
                    viewScormResultForStudent(
                      currentCourse.id,
                      coupledTestMod?.moduleId || mod.moduleId,
                      currentCourse.courseName,
                      mod.name,
                      stud.studentId,
                      stud.name
                    );
                  }
                  queryClient.invalidateQueries({ queryKey: [
                    "FetchCoursesByClassId",
                    classData.id,
                  ] });
                }}
              >
                <FormattedMessage id="ShowResult" />
              </Button>
            );

            const showResult = () => {
              //Gy 25 modules
              if (isGy25FactModule && coupledTestMod) {
                const testHasRun = studentTestMod?.lastRun != null;
                if (!testHasRun) return <></>;

                return renderButton();
              }

              // Gy 25 fact module with no coupled test module
              if (isGy25FactModule && !coupledTestMod) {
                return <></>;
              }

              // Other modules
              const moduleHasNotRun = stud.lastRun === null;
              if (moduleHasNotRun) return <></>;

              return renderButton();
            };

            let student = {
              studentId: studentToAdd.studentId,
              id: "s" + studentToAdd.studentId + "mod" + mod.moduleId,
              parentId: "m" + mod.moduleId,
              showResult: showResult(),
              name: studentToAdd.name,
              sortName: studentToAdd.name.toLowerCase(),
              status: getStatusBadge(),
              statusCode:
                coupledTestMod != null && studentTestMod != null
                  ? studentTestMod.status
                  : studentToAdd.status
                    ? studentToAdd.status
                    : "NotStarted",
              score: getModuleScore(),
              nbrOfAttempts: getNbrOfAttempt(),
              focusLostCount: mod.isGy25
                ? buildFocusLossCell(
                    studentTestMod?.focusLosses || studentToAdd.focusLosses,
                    studentToAdd.name,
                    mod.name,
                  )
                : null,
              activityPoints: getActivityPoints(),
              lastRun: getLastRun(),
              scorePercent: studentToAdd.score
                ? Math.round((studentToAdd.score / studentToAdd.maxScore) * 100)
                : 0,
              activityPointsPercentage:
                mod.isGy25 && mod.isFinalExam
                  ? null
                  : studentToAdd.activityPoints
                    ? Math.round(
                        (studentToAdd.activityPoints /
                          studentToAdd.maxActivityPoints) *
                        100
                      )
                    : 0,
            };

            tableData.push(student);
          });
        }
      });

      const sortedTableData = sortStudentsByName(tableData);

      setResultTableData(sortedTableData);
    },

    [
      buildExamRowForClass,
      buildExamRowForStudent,
      classCoursesData.data,
      classData.id,
      finalExamVariants.data,
      intl,
      queryClient,
    ]
  );

  useEffect(() => {
    if (!classCoursesData.isPending && classCoursesData.data?.length > 0) {
      if (!selectedCourse) {
        setSelectedCourse(classCoursesData.data[0]);
      }
    }
  }, [classCoursesData.isPending, classCoursesData.data, selectedCourse]);

  useEffect(() => {
    if (!selectedCourse) return;
    if (finalExamVariants.isPending) return;

    setResultData(selectedCourse);
    setChartData(selectedCourse);
  }, [
    selectedCourse,
    finalExamVariants.isPending,
    setResultData,
    setChartData,
  ]);

  if (classCoursesData.isPending || finalExamVariants.isPending) {
    return (
      <div className="inline-pacman-loader">
        <PacmanLoader color="#20a8d8" size={20} />
      </div>
    );
  }

  let columnExtensions = [
    { columnName: "name", width: 280 },
    { columnName: "showResult", width: 160 },
    { columnName: "status", width: 190 },
    { columnName: "score", width: 150 },
    { columnName: "nbrOfAttempts", width: 150 },
    { columnName: "focusLostCount", width: 130 },
    { columnName: "activityPoints", width: 160 },
    { columnName: "lastRun", width: 200 },
  ];

  let columns = [
    { title: <FormattedMessage id="Name" />, name: "name" },
    { title: <FormattedMessage id="ShowResult" />, name: "showResult" },
    { title: <FormattedMessage id="Status" />, name: "status" },
    { title: <FormattedMessage id="ModuleTestScore" />, name: "score" },
    {
      title: <FormattedMessage id="NumberOfAttempts" />,
      name: "nbrOfAttempts",
    },
    {
      title: <FormattedMessage id="FocusControl" />,
      name: "focusLostCount",
    },
    { title: <FormattedMessage id="ActivityPoints" />, name: "activityPoints" },
    { title: <FormattedMessage id="LastRun" />, name: "lastRun" },
  ];

  const courses = [];
  classCoursesData.data?.forEach((c) => {
    let course = { id: c.id, courseName: c.courseName };
    courses.push(course);
  });

  var studentIdArray = [];
  classData.students.forEach((student) => {
    studentIdArray.push(student.id);
  });

  const handleChangedCourse = (value) => {
    const nextCourse = classCoursesData.data?.find((c) => c.id === value.value);

    setSelectedCourse(nextCourse);
    setShowStudentResult(false);
  };

  return (
    <div>
      <Row>
        <Col lg={4} style={{ zIndex: "999" }}>
          <Select
            value={
              selectedCourse
                ? { label: selectedCourse.courseName, value: selectedCourse.id }
                : null
            }
            options={courses.map((option) => ({
              ...option,
              label: option.courseName,
              value: option.id,
            }))}
            onChange={(value) => {
              handleChangedCourse(value);
            }}
          />
        </Col>
      </Row>
      <div>
        <CourseModulesResultsBar
          primaryData={primaryModulesData}
          secondaryData={secondaryModulesData}
        />
      </div>
      {moduleId && courseId && showClassResult && (
        <ClassResultQuestionOverview
          classId={classData.id}
          students={classData.students}
          courseId={courseId}
          moduleId={moduleId}
          moduleName={moduleName}
          courseName={courseName}
          resultIsOpen={classResultIsOpen}
          toggle={toggle}
          isGy25FinalExam={isGy25FinalExam}
          variantId={finalExamVariantId}
        />
      )}
      {showStudentResult && (
        <StudentResultQuestionOverview
          studentId={studentForScormResults.id}
          courseId={courseId}
          moduleId={moduleId}
          moduleName={moduleName}
          courseName={studentForScormResults.name}
          bestResultIsOpen={studentBestResultIsOpen}
          toggleBest={toggleBestStudentResult}
          latestResultIsOpen={studentLatestResultIsOpen}
          toggleLatest={toggleLatestStudentResult}
          isGy25FinalExam={isGy25FinalExam}
          variantId={finalExamVariantId}
        />
      )}
      {resultTableData !== null ? (
        <ResultTable
          level="Class"
          tableData={resultTableData}
          columnExtensions={columnExtensions}
          columns={columns}
          rightAlignedColumns={[
            "score",
            "nbrOfAttempts",
            "focusLostCount",
            "activityPoints",
          ]}
          centerAlignedColumns={["status"]}
        />
      ) : (
        ""
      )}
      <FocusLossModal
        isOpen={focusLossModal.isOpen}
        onClose={closeFocusLossModal}
        runData={focusLossModal.runData}
        label={focusLossModal.label}
        moduleName={focusLossModal.moduleName}
        isFinalExam={focusLossModal.isFinalExam}
      />
      {invalidateAccessPrompt}
    </div>
  );
}
export default ClassResultOverview;
